import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import type {
  QuizMetaAnswer,
  QuizMetaEntrypoint,
  QuizMetaQuestion,
} from "./types";

export interface QuizStepStandardProps {
  step: QuizMetaQuestion | QuizMetaEntrypoint;
  answers: QuizMetaAnswer[];
  isThirdPerson: boolean;
  isMultipleChoice: boolean;
  selectedAnswers: Record<string, unknown>;
  setSelectedAnswers: (selected: string[]) => void;
  error: string | null;
}

export const QuizStepStandard: React.FC<QuizStepStandardProps> = ({
  step,
  isThirdPerson,
  isMultipleChoice,
  selectedAnswers,
  setSelectedAnswers,
  error,
  answers,
}) => {
  const title = isThirdPerson
    ? "titleAnotherAdult" in step
      ? step.titleAnotherAdult
      : step.title
    : step.title ?? "";

  const stepHandle = step.handle as string;

  const selectedAnswersForStep =
    (selectedAnswers[stepHandle] as unknown as string[]) ?? [];

  return (
    <motion.li
      className="step"
      data-step={stepHandle}
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="quiz-inner">
        <div className="quiz-title" data-question-type="title">
          <h2>{title}</h2>
        </div>
        {isMultipleChoice && (
          <div className="quiz-multiple-choice quiz-caption">
            <p>Select as many as you like.</p>
          </div>
        )}
        {"caption" in step && step.caption && (
          <div className="quiz-caption" data-question-type="caption">
            <p>{step.caption}</p>
          </div>
        )}
        <div data-question-type="input-variable">
          <ol className="quiz-cards">
            {answers?.map((answer) => (
              <li
                key={answer.handle}
                className={`quiz-card-radio ${
                  selectedAnswersForStep.includes(answer.handle as string)
                    ? "selected"
                    : ""
                }`}
              >
                <label htmlFor={`step-${step.handle}-radio-${answer.handle}`}>
                  <input
                    type={isMultipleChoice ? "checkbox" : "radio"}
                    name={`quiz-step-${stepHandle}`}
                    id={`step-${step.handle}-radio-${answer.handle}`}
                    value={answer.handle}
                    checked={selectedAnswersForStep.includes(answer.handle)}
                    onChange={(e) => {
                      const answerHandle = answer.handle as string;
                      const existingAnswers = selectedAnswers[
                        stepHandle
                      ] as unknown as string[];

                      if (!answerHandle) return;

                      if (e.target.checked) {
                        if (isMultipleChoice) {
                          setSelectedAnswers([
                            ...(existingAnswers ?? []),
                            answerHandle,
                          ]);
                        } else {
                          setSelectedAnswers([answerHandle]);
                        }
                      } else {
                        setSelectedAnswers(
                          (existingAnswers ?? []).filter(
                            (v) => v !== answerHandle
                          )
                        );
                      }
                    }}
                  />
                  <div className="quiz-label">{answer.label}</div>
                </label>
              </li>
            ))}
          </ol>
        </div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key={error}
              className="quiz-error"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.li>
  );
};
